import React, { useCallback, useRef, useState } from "react";
import classNames from "classnames";
import { useKeyboardCommand } from "@hooks/keyboard/useKeyboardCommand";
import { useDebouncedEffect } from "@hooks/useDebouncedEffect";
import { SearchIcon } from "@icons";
import { SEARCH_DEBOUNCE_TIME } from "@constants";
import { Input } from "@design-system/Input/Input";
import styles from "./SearchFilter.module.scss";

interface SearchFilterProps {
    filter: string | undefined;
    onChange(value: string | undefined): void;
    minLength?: number;
    placeholder?: string;
    dataTestId?: string;
    containerClassName?: string;
    debounceTime?: number;
    keyboardShortcuts?: boolean;
    hasFindShortcut?: boolean;
    disabled?: boolean;
}

export const SearchFilter: React.FC<SearchFilterProps> = ({
    filter,
    onChange,
    placeholder = "Search...",
    dataTestId = "search-filter",
    minLength = 3,
    containerClassName,
    debounceTime = SEARCH_DEBOUNCE_TIME,
    keyboardShortcuts = false,
    hasFindShortcut = false,
    disabled = false,
}) => {
    const [value, setValue] = useState<string>(filter ?? "");
    const [isFocused, setIsFocused] = useState(false);
    const prevValue = useRef(value);
    const inputRef = useRef<HTMLInputElement>(null);

    const debounceFn = useCallback(() => {
        if (prevValue.current === value) {
            return;
        }
        prevValue.current = value;
        if (value.length >= minLength) {
            onChange(value);
        } else {
            onChange(undefined);
        }
    }, [minLength, onChange, value]);

    useDebouncedEffect(debounceFn, [debounceFn], debounceTime);

    const [prevFilter, setPrevFilter] = useState(filter);
    if (filter !== prevFilter) {
        setPrevFilter(filter);
        if (!filter) {
            if (value.length >= minLength) {
                setValue("");
            }
        } else if (value === "") {
            setValue(filter);
        }
    }

    useKeyboardCommand({
        keys: ["Escape", "Enter"],
        callback: () => {
            inputRef.current?.blur();
        },
        options: {
            description: "Defocus search",
            preventDefault: true,
            enabled: keyboardShortcuts && isFocused,
            enableOnFormTags: true,
        },
    });

    const focusSearch = () => {
        inputRef.current?.focus();
        inputRef.current?.select();
    };

    useKeyboardCommand({
        keys: "alt+s",
        callback: focusSearch,
        options: {
            description: "Focus search",
            enabled: keyboardShortcuts && !disabled,
            preventDefault: true,
        },
    });

    useKeyboardCommand({
        keys: "mod+f",
        callback: focusSearch,
        options: {
            description: "Find in table",
            enabled: hasFindShortcut && !disabled,
            preventDefault: true,
            enableOnFormTags: true,
        },
    });

    return (
        <Input
            size="md"
            className={classNames(
                styles.root,
                isFocused && styles.focused,
                containerClassName,
            )}
            leftIcon={<SearchIcon />}
            clearable
            value={value}
            onChange={(event) => setValue(event.target.value)}
            placeholder={placeholder}
            data-testid={dataTestId}
            disabled={disabled}
            ref={inputRef}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
        />
    );
};
